"use client";

import { CMSData } from "@/cms/models";
import { useRouter } from "next/navigation";
import React from "react";
import Button from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-light-svg-icons";
import { twMerge } from "tailwind-merge";

interface SearchBarComponentProps {
  config?: {
    cmsData: CMSData;
    input?: {
      placeholderText?: string;
      placeholderTextColor?: string;
      bgColor?: string;
      textColor?: string;
      borderColor?: string;
      focusBorderColor?: string;
    };
    button?: {
      bgColor?: string;
      textColor?: string;
      hoverBgColor?: string;
      hoverTextColor?: string;
    };
  };
  toggleSearch?: () => void;
}

const SearchBarComponent: React.FC<SearchBarComponentProps> = ({
  config,
  toggleSearch,
}) => {
  const { cmsData } = config || {};

  const router = useRouter();
  const [formData, setFormData] = React.useState({ q: "" });
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    toggleSearch?.();

    const query = formData.q.trim() ?? "";
    setFormData({ q: "" });

    const currentRoute = location?.pathname;
    const page = cmsData?.pages.find((x) => x.slug === currentRoute);
    const hasCaseList = page?.sections.some((x) => x.type === "CaseList");
    let defaultSearchPage = hasCaseList
      ? currentRoute
      : (cmsData?.defaultSearchPage ?? "/koeb");

    if (!defaultSearchPage.startsWith("/")) {
      defaultSearchPage = `/${defaultSearchPage}`;
    }

    if (formData.q.trim()) {
      router.push(`${defaultSearchPage}?q=${encodeURIComponent(query)}`);
    } else {
      router.push(defaultSearchPage);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      className="w-full bg-white p-2 flex"
    >
      <input
        type="text"
        id="q"
        name="q"
        style={
          {
            "--text-color":
              config?.input?.textColor ?? "var(--input-text-color)",
            "--bg-color": config?.input?.bgColor ?? "var(--input-bg-color)",
            "--border-color": config?.input?.borderColor ?? "",
            "--focus-border-color":
              config?.input?.focusBorderColor ?? "var(--input-text-color)",
            "--placeholder-text-color":
              config?.input?.placeholderTextColor ?? "text-gray-800",
          } as React.CSSProperties
        }
        className={twMerge(
          config?.input?.borderColor ? "border" : "",
          "p-2 w-full text-gray-800 focus:outline-none bg-[var(--bg-color)] text-[var(--text-color)] border-[var(--border-color)] focus:border-[var(--focus-border-color)] placeholder-[var(--placeholder-text-color)]",
          "placeholder:bg-green-[var(--placeholder-text-color)]"
        )}
        placeholder={
          config?.input?.placeholderText ??
          "Søg på sagsnr., by vej, postnr eller kommune"
        }
        value={formData.q}
        onChange={handleChange}
      />
      <Button
        type="submit"
        style={
          {
            "--search-button-bg-color": config?.button?.bgColor ?? "inherit",
            "--search-button-text-color":
              config?.button?.textColor ?? "inherit",
            "--search-button-hover-bg-color":
              config?.button?.hoverBgColor ?? "inherit",
            "--search-button-hover-text-color":
              config?.button?.hoverTextColor ?? "inherit",
          } as React.CSSProperties
        }
        className="bg-[var(--search-button-bg-color)] text-[var(--search-button-text-color)] hover:bg-[var(--search-button-hover-bg-color)] hover:text-[var(--search-button-hover-text-color)]"
      >
        <FontAwesomeIcon className="size-[24px]" icon={faMagnifyingGlass} />
        Søg
      </Button>
    </form>
  );
};

export default SearchBarComponent;
